import React, { Suspense } from "react";
import { Box, Paper, Typography, useMediaQuery } from "@mui/material";
import SideBar from "./SideBar";
import { Navigate, Outlet } from "react-router-dom";

import PageSkeleton from "../skeletons/PageSkeleton";
import DashboardSkeletonMobile from "../skeletons/DashboardSkeletonMobile";
import PrintRequestsSkeletonMobile from "../skeletons/PrintRequestsSkeletonMobile";
import HistorySkeletonMobile from "../skeletons/HistorySkeletonMobile";

import {
  initPrintRequestStream,
  closePrintRequestStream,
} from "../../services/printRequest.service";
import { StoreProvider, useStoreState } from "../../contexts/StoreContext";

import NewLoader from "../../assets/images/newlogo_loader.gif";
import useLayoutWrapper from "../../hooks/useLayoutWrapper";

import MobileBottomNav from "./MobileBottomNav";
import MobileHeader from "./MobileHeader";

import { useLocation } from "react-router-dom";

const InnerLayout = () => {
  const { isAuthenticated, userId, storeId, loading } = useLayoutWrapper();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const location = useLocation();
  const isDashboard = location.pathname === "/" || location.pathname === "/dashboard";
  const isPrintRequests = location.pathname === "/requests";
  const isHistory = location.pathname === "/history";

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (loading) {
    return (
      <Box
        display="flex"
        height="100vh"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={2}
      >
        <img src={NewLoader} alt="Loading..." style={{ width: 80, height: 80 }} />
        <Typography variant="body1" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!storeId) {
    return (
      <Box
        display="flex"
        height="100vh"
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="h6" color="text.secondary">
          No store assigned
        </Typography>
      </Box>
    );
  }

  const renderFallback = () => {
    if (isMobile) {
      if (isDashboard) {
        return <DashboardSkeletonMobile />;
      } else if (isPrintRequests) {
        return <PrintRequestsSkeletonMobile />;
      } else if (isHistory) {
        return <HistorySkeletonMobile />;
      }
    }
    
    return <PageSkeleton />;
  };

  return (
    <Box display="flex" height="100vh" overflow="hidden" flexDirection="column">
      {/* Mobile Header */}
      {isMobile && <MobileHeader showBack={false} />}

      {/* Main flex container */}
      <Box display="flex" flex={1} overflow="hidden">
        {/* Sidebar only on desktop */}
        {!isMobile && <SideBar />}

        {/* Main Content */}
        <Box
          component={Paper}
          flex={1}
          px={isMobile ? (isDashboard ? 0 : 0) : 4}
          pt={isMobile ? (isDashboard ? 8 : 7) : 2}
          pb={isMobile ? (isDashboard ? 5 : 0) : 2}
          sx={{
            overflowX: "auto",
            borderRadius: 0,
            background:
              "linear-gradient(to bottom,rgba(255, 213, 0, 0.09) 0%,rgba(255, 255, 255, 0) 20%)",
          }}
        >
          <Suspense fallback={renderFallback()}>
            <Outlet />
          </Suspense>
        </Box>
      </Box>

      {/* Mobile Bottom Nav */}
      {isMobile && <MobileBottomNav />}
    </Box>
  );
};

const LayoutWrapper = () => {
  const { isAuthenticated, userId } = useLayoutWrapper();

  return (
    <StoreProvider userId={userId} isAuthenticated={isAuthenticated}>
      <InnerLayout />
    </StoreProvider>
  );
};

export default LayoutWrapper;
